import { SUPPORTED_LOCALES, type Direction, type Locale } from "./types";

export const LOCALE_STORAGE_KEY = "source-sand.locale.v1";

const localeSet = new Set<string>(SUPPORTED_LOCALES);
const intlLocales: Readonly<Record<Locale, string>> = {
  en: "en-GB",
  ar: "ar-AE",
  ru: "ru-RU",
  ro: "ro-RO"
};

type ReadableStorage = Pick<Storage, "getItem">;
type WritableStorage = Pick<Storage, "setItem">;

export type LocaleDetectionOptions = {
  storage?: ReadableStorage | null;
  languages?: readonly string[];
  language?: string;
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && localeSet.has(value);
}

export function directionForLocale(locale: Locale): Direction {
  return locale === "ar" ? "rtl" : "ltr";
}

export function toIntlLocale(locale: Locale): string {
  return intlLocales[locale];
}

function browserStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function browserLanguages(): readonly string[] {
  if (typeof navigator === "undefined") {
    return [];
  }

  try {
    return Array.isArray(navigator.languages) ? navigator.languages : [];
  } catch {
    return [];
  }
}

function browserLanguage(): string {
  if (typeof navigator === "undefined") {
    return "";
  }

  try {
    return navigator.language ?? "";
  } catch {
    return "";
  }
}

function baseLocale(value: string): Locale | null {
  const base = value.trim().toLocaleLowerCase("en-US").split(/[-_]/, 1)[0];
  return isLocale(base) ? base : null;
}

export function detectLocale(options: LocaleDetectionOptions = {}): Locale {
  const storage =
    Object.prototype.hasOwnProperty.call(options, "storage")
      ? (options.storage ?? null)
      : browserStorage();

  if (storage) {
    try {
      const persisted = storage.getItem(LOCALE_STORAGE_KEY);
      if (isLocale(persisted)) {
        return persisted;
      }
    } catch {
      // Locale detection must remain available when storage is blocked.
    }
  }

  const languages =
    options.languages === undefined ? browserLanguages() : options.languages;
  const language =
    options.language === undefined ? browserLanguage() : options.language;

  for (const candidate of [...languages, language]) {
    const locale = baseLocale(candidate);
    if (locale) {
      return locale;
    }
  }

  return "en";
}

export function persistLocale(
  locale: Locale,
  storage: WritableStorage | null = browserStorage()
): boolean {
  if (!storage) {
    return false;
  }

  try {
    storage.setItem(LOCALE_STORAGE_KEY, locale);
    return true;
  } catch {
    return false;
  }
}

export function applyDocumentLocale(locale: Locale, root?: HTMLElement): void {
  const documentRoot =
    root ?? (typeof document === "undefined" ? undefined : document.documentElement);

  if (!documentRoot) {
    return;
  }

  const direction = directionForLocale(locale);
  if (documentRoot.lang !== locale) {
    documentRoot.lang = locale;
  }
  if (documentRoot.dir !== direction) {
    documentRoot.dir = direction;
  }
  if (documentRoot.dataset.locale !== locale) {
    documentRoot.dataset.locale = locale;
  }
}

export function initializeLocale(options?: LocaleDetectionOptions): Locale {
  const locale = detectLocale(options);
  applyDocumentLocale(locale);
  return locale;
}
